<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CONTENT_TYPES } from '../constants.js';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';

const { content, contentAttributes, contentType, inboxId } = useMessageContext();
const { t } = useI18n();
const store = useStore();
const inbox = computed(() => store.getters['inboxes/getInbox'](inboxId.value));
const { isAWebWidgetInbox } = useInbox(inbox);

// Available options (items sent to the customer)
const interactiveItems = computed(() => {
  if (contentType.value !== CONTENT_TYPES.INPUT_SELECT) return [];
  return contentAttributes.value?.items ?? [];
});

// Whether options were sent as buttons (≤3) or list (4+)
const interactiveLabel = computed(() => {
  const count = interactiveItems.value.length;
  if (count === 0) return '';
  return count <= 3 ? 'Botões' : 'Lista';
});

const formValues = computed(() => {
  if (contentType.value === CONTENT_TYPES.FORM) {
    const { items, submittedValues = [] } = contentAttributes.value;

    if (submittedValues.length) {
      return submittedValues.map(submittedValue => {
        const item = items.find(
          formItem => formItem.name === submittedValue.name
        );
        return {
          title: submittedValue.value,
          value: submittedValue.value,
          label: item?.label,
        };
      });
    }

    return [];
  }

  if (contentType.value === CONTENT_TYPES.INPUT_SELECT) {
    const [item] = contentAttributes.value?.submittedValues ?? [];
    if (!item) return [];

    return [
      {
        title: item.title,
        value: item.value,
        label: '',
      },
    ];
  }

  return [];
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="csat">
    <span v-dompurify-html="content" :title="content" />

    <!-- Interactive options (buttons/list items) sent to the customer -->
    <div v-if="interactiveItems.length" class="interactive-options-section">
      <div class="interactive-options-label">
        <span class="interactive-options-icon">📋</span>
        <span>{{ interactiveLabel }}</span>
      </div>
      <div class="interactive-options-list">
        <span
          v-for="item in interactiveItems"
          :key="item.value"
          class="interactive-option-pill"
        >
          {{ item.title }}
        </span>
      </div>
    </div>

    <!-- Customer's submitted response -->
    <dl v-if="formValues.length" class="mt-4">
      <template v-for="item in formValues" :key="item.title">
        <dt class="text-n-slate-11 italic mt-2">
          {{ item.label || t('CONVERSATION.RESPONSE') }}
        </dt>
        <dd>{{ item.title }}</dd>
      </template>
    </dl>
    <div v-else-if="isAWebWidgetInbox" class="my-2 font-medium">
      {{ t('CONVERSATION.NO_RESPONSE') }}
    </div>
  </BaseBubble>
</template>

<style scoped>
.interactive-options-section {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.interactive-options-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 500;
  opacity: 0.6;
  margin-bottom: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.interactive-options-icon {
  font-size: 0.75rem;
}

.interactive-options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.interactive-option-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.15);
  line-height: 1.4;
}
</style>

